import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ShieldOff, KeyRound, Copy, Check, AlertTriangle, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TwoFactorSettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [setup, setSetup] = useState(null); // {secret, qr_data_url, backup_codes}
  const [confirmCode, setConfirmCode] = useState("");
  const [disablePwd, setDisablePwd] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [copied, setCopied] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/2fa/status`, { withCredentials: true });
      setStatus(data);
    } catch {
      setStatus({ enabled: false, backup_codes_remaining: 0 });
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const startSetup = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/setup`, {}, { withCredentials: true });
      setSetup(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore avvio setup 2FA");
    } finally {
      setLoading(false);
    }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/confirm`, { code: confirmCode.trim() }, { withCredentials: true });
      toast.success("2FA attivata con successo");
      setSetup(null);
      setConfirmCode("");
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Codice non valido");
    } finally {
      setLoading(false);
    }
  };

  const disable2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/2fa/disable`,
        { password: disablePwd, code: disableCode.trim() },
        { withCredentials: true },
      );
      toast.success("2FA disattivata");
      setDisablePwd("");
      setDisableCode("");
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Disattivazione fallita");
    } finally {
      setLoading(false);
    }
  };

  const copyAll = () => {
    const text = setup.backup_codes.join("\n");
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Codici copiati negli appunti");
  };

  if (!status) {
    return (
      <div className="flex items-center gap-2 text-sm text-zinc-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Caricamento stato 2FA...
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6" data-testid="2fa-settings">
      <div className="flex items-start gap-4 p-5 rounded-2xl border border-[#E5E2DB] bg-white">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${status.enabled ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>
          {status.enabled ? <ShieldCheck className="w-6 h-6" /> : <ShieldOff className="w-6 h-6" />}
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-lg font-bold text-[#0F1F14]">
            Autenticazione a due fattori (2FA)
          </h3>
          <p className="text-sm text-zinc-600 mt-1">
            {status.enabled
              ? <>Attiva dal <span className="font-semibold">{(status.activated_at || "").split("T")[0]}</span>. {status.backup_codes_remaining} codici di recupero ancora disponibili.</>
              : "Aggiungi un secondo livello di sicurezza al tuo account admin usando un'app authenticator (Google Authenticator, Authy, 1Password)."}
          </p>
          {!status.enabled && !setup && (
            <Button
              onClick={startSetup} disabled={loading}
              className="mt-4 rounded-full bg-[#163321] text-white hover:bg-[#224A32] h-10 text-sm"
              data-testid="2fa-start-btn">
              {loading ? "Generazione..." : <><ShieldCheck className="w-4 h-4 mr-2" /> Attiva 2FA</>}
            </Button>
          )}
        </div>
      </div>

      {/* Setup flow */}
      {setup && (
        <div className="p-5 rounded-2xl border border-[#D97B58]/30 bg-[#FFF8F3] space-y-5" data-testid="2fa-setup-panel">
          <div>
            <h4 className="font-heading text-base font-bold text-[#0F1F14] mb-1">1. Scansiona il QR code</h4>
            <p className="text-xs text-zinc-600 mb-3">
              Apri la tua app authenticator e scansiona questo codice. Oppure inserisci manualmente il secret: <code className="px-2 py-0.5 bg-zinc-100 rounded text-[11px]">{setup.secret}</code>
            </p>
            <div className="inline-block p-3 bg-white rounded-xl border border-zinc-200">
              <img src={setup.qr_data_url} alt="QR code 2FA" className="w-48 h-48" data-testid="2fa-qr" />
            </div>
          </div>

          <div>
            <h4 className="font-heading text-base font-bold text-[#0F1F14] mb-1">2. Salva i codici di recupero</h4>
            <div className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-amber-900">
                Conserva questi codici in un luogo sicuro (password manager, carta stampata). Ognuno e' utilizzabile <strong>una sola volta</strong> e NON verranno mostrati di nuovo.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {setup.backup_codes.map((c, i) => (
                <div key={i} className="font-mono text-sm px-3 py-2 bg-white border border-zinc-200 rounded text-center" data-testid={`backup-code-${i}`}>
                  {c}
                </div>
              ))}
            </div>
            <Button onClick={copyAll} variant="outline" className="rounded-full h-9 text-xs" data-testid="copy-backup-btn">
              {copied ? <><Check className="w-3.5 h-3.5 mr-1.5" /> Copiati</> : <><Copy className="w-3.5 h-3.5 mr-1.5" /> Copia tutti</>}
            </Button>
          </div>

          <form onSubmit={confirmSetup} className="space-y-3 pt-3 border-t border-zinc-200">
            <div>
              <h4 className="font-heading text-base font-bold text-[#0F1F14] mb-1">3. Conferma il codice</h4>
              <Label htmlFor="confirm-code" className="text-xs text-zinc-600">Inserisci il codice a 6 cifre generato dall'app</Label>
              <Input
                id="confirm-code" type="text" inputMode="numeric" maxLength={6}
                value={confirmCode} onChange={e => setConfirmCode(e.target.value)}
                placeholder="123456" required
                className="mt-1 h-12 text-center text-xl tracking-[0.4em] font-mono max-w-xs"
                data-testid="confirm-code-input"
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={loading || confirmCode.length !== 6} className="rounded-full bg-[#163321] text-white hover:bg-[#224A32] h-10" data-testid="confirm-2fa-btn">
                {loading ? "Verifica..." : "Conferma e attiva"}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setSetup(null); setConfirmCode(""); }} className="rounded-full h-10">
                Annulla
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Disable flow */}
      {status.enabled && (
        <form onSubmit={disable2FA} className="p-5 rounded-2xl border border-red-200 bg-red-50/50 space-y-3" data-testid="2fa-disable-panel">
          <h4 className="font-heading text-base font-bold text-red-900 flex items-center gap-2">
            <ShieldOff className="w-4 h-4" /> Disattiva 2FA
          </h4>
          <p className="text-xs text-red-800">Per disattivare la protezione a due fattori conferma la tua password e il codice corrente dall'authenticator (oppure un codice di recupero).</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="disable-pwd" className="text-xs text-zinc-700">Password account</Label>
              <Input id="disable-pwd" type="password" value={disablePwd} onChange={e => setDisablePwd(e.target.value)} required data-testid="disable-pwd-input" className="h-10 mt-1" />
            </div>
            <div>
              <Label htmlFor="disable-code" className="text-xs text-zinc-700">Codice 2FA</Label>
              <Input
                id="disable-code" type="text" inputMode="text" maxLength={11}
                value={disableCode} onChange={e => setDisableCode(e.target.value)}
                placeholder="123456" required data-testid="disable-code-input"
                className="h-10 mt-1 font-mono"
              />
            </div>
          </div>
          <Button type="submit" disabled={loading} variant="destructive" className="rounded-full h-10 text-sm" data-testid="disable-2fa-btn">
            <ShieldOff className="w-4 h-4 mr-2" /> Disattiva 2FA
          </Button>
        </form>
      )}

      {/* Info box */}
      <div className="text-xs text-zinc-500 flex items-start gap-2 p-3 bg-zinc-50 rounded-lg">
        <KeyRound className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <p>
          App consigliate: <strong>Google Authenticator</strong>, <strong>Authy</strong>, <strong>1Password</strong>, <strong>Microsoft Authenticator</strong>. Il secret e i backup codes sono salvati cifrati nel database (AES-128 via Fernet).
        </p>
      </div>
    </div>
  );
}
